import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

const DYNAMIC_SPACE_MIN_HEIGHT = 50
const SCROLL_OFFSET_FROM_TOP = 95
const HEADER_AND_INPUT_OFFSET = 100

export interface ScrollBufferProps {
  containerRef: React.RefObject<HTMLDivElement>
  scrollableSelector: string
  trigger?: any
}

const ScrollBuffer = forwardRef<HTMLDivElement, ScrollBufferProps>(
  ({ containerRef, scrollableSelector, trigger }, ref) => {
    const dynamicSpaceRef = useRef<HTMLDivElement>(null)
    const [dynamicSpaceHeight, setDynamicSpaceHeight] = useState<number>(0)

    const updateDynamicSpaceHeight = useCallback(() => {
      if (!containerRef.current) return

      const scrollableContainer = containerRef.current.closest(
        scrollableSelector
      ) as HTMLElement
      if (!scrollableContainer) return

      const messages = containerRef.current.querySelectorAll(
        '.thanx-ai-conversation__message'
      )
      if (messages.length === 0) {
        setDynamicSpaceHeight(0)
        return
      }

      const chatArea = containerRef.current.querySelector(
        '.thanx-ai-conversation__chat-area'
      ) as HTMLElement
      if (!chatArea) return

      // Find last user message
      let lastUserMessage: HTMLElement | null = null
      for (let i = messages.length - 1; i >= 0; i--) {
        const message = messages[i] as HTMLElement
        const isUser = !message.classList.contains(
          'thanx-ai-conversation__message--ai'
        )
        const isTool = message.classList.contains(
          'thanx-ai-conversation__message--tool_call'
        )
        if (isUser && !isTool) {
          lastUserMessage = message
          break
        }
      }

      if (!lastUserMessage) {
        setDynamicSpaceHeight(0)
        return
      }

      // Calculate required space
      const containerRect = scrollableContainer.getBoundingClientRect()
      const msgRect = lastUserMessage.getBoundingClientRect()

      const availableChatHeight =
        scrollableContainer.clientHeight - HEADER_AND_INPUT_OFFSET
      const spaceNeededForScrolling =
        availableChatHeight - SCROLL_OFFSET_FROM_TOP

      const chatAreaForCalc = containerRef.current.querySelector(
        '.thanx-ai-conversation__chat-area'
      ) as HTMLElement
      const chatAreaRect = chatAreaForCalc
        ? chatAreaForCalc.getBoundingClientRect()
        : containerRect
      const spaceNaturallyBelow = chatAreaRect.bottom - msgRect.bottom

      const requiredSpace = Math.max(
        0,
        spaceNeededForScrolling - spaceNaturallyBelow
      )
      let newHeight = Math.max(DYNAMIC_SPACE_MIN_HEIGHT, requiredSpace)

      setDynamicSpaceHeight(newHeight)

      // Update DOM ref
      if (dynamicSpaceRef.current) {
        dynamicSpaceRef.current.style.height = `${newHeight}px`
      }
    }, [containerRef, scrollableSelector])

    // Expose height update method for parent to call
    const updateHeight = useCallback(() => {
      updateDynamicSpaceHeight()
    }, [updateDynamicSpaceHeight])

    // Update height when trigger changes
    useEffect(() => {
      updateDynamicSpaceHeight()
    }, [trigger, updateDynamicSpaceHeight])

    // Handle window resize
    useEffect(() => {
      const handleResize = () => updateDynamicSpaceHeight()
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [updateDynamicSpaceHeight])

    // Handle scroll events to update height when user scrolls
    useEffect(() => {
      if (!containerRef.current) return

      const scrollableContainer = containerRef.current.closest(
        scrollableSelector
      ) as HTMLElement
      if (!scrollableContainer) return

      const handleScroll = () => updateDynamicSpaceHeight()
      scrollableContainer.addEventListener('scroll', handleScroll)
      return () =>
        scrollableContainer.removeEventListener('scroll', handleScroll)
    }, [containerRef, scrollableSelector, updateDynamicSpaceHeight])

    // Forward ref to the dynamic space div
    useImperativeHandle(ref, () => dynamicSpaceRef.current!, [])

    // Expose methods via ref (for parent component)
    useEffect(() => {
      if (containerRef.current) {
        ;(containerRef.current as any)._updateScrollBuffer = updateHeight
      }
    }, [updateHeight, containerRef])

    return (
      <div
        ref={dynamicSpaceRef}
        style={{ height: `${dynamicSpaceHeight}px` }}
      />
    )
  }
)

export default ScrollBuffer
